/**
 * A tab opened before a release asks for the old release's code-split files, which the new release no longer serves:
 * the lazy import fails (404) and the route shows "Something went wrong" (seen in production on 2026-10-05). Reloading
 * fetches the new index and its files. At most one automatic reload every 30 seconds, so a file that is really
 * missing can't loop.
 */
const KEY = 'umrah:stale-build-reload'
const QUIET_MS = 30_000

/** Whether an error is a code-split file that failed to load (each browser words it differently). */
export function isStaleBuildError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(message)
}

/** Whether an automatic reload may happen now (none in the last 30 seconds). Reads only. */
export function mayReloadForNewBuild(): boolean {
  try {
    return Date.now() - Number(window.sessionStorage.getItem(KEY) ?? '0') >= QUIET_MS
  } catch {
    return false // No session storage (blocked): nothing would stop a loop, so no automatic reload.
  }
}

/** Reloads the page for the new release unless one was just tried; returns whether it is reloading. */
export function reloadForNewBuild(): boolean {
  if (!mayReloadForNewBuild()) return false
  try {
    window.sessionStorage.setItem(KEY, String(Date.now()))
  } catch {
    return false
  }
  window.location.reload()
  return true
}
